import React from 'react';
import { BiSearchAlt } from 'react-icons/bi';
import { defineGlobalSearch, type ScopeOption } from '@hopdrive/feature-global-search';
import PageShell from '@components/PageShell';
import EmptyState from '@components/EmptyState';
import DataCard from '@components/DataCard';
import { FeaturePermission, useFeaturePermission } from '@utils/featurePermissions';
import { useIsHasuraAdmin } from '@utils/hasuraRole';
import { INTERNAL_ROLES, getRoleLabel } from '@features/users/utils/roleUtils';
import { useGetActiveCustomersForSelectQuery } from '@features/customers/gql/queries.generated';
import { resolveAssetUrl } from '@features/learningModule/utils/upload';
import RooftopFilter from './components/RooftopFilter';

/**
 * Admin's global search configuration.
 *
 * The feature owns the capability — documents, entities, ranking, the results
 * page (#1237). This file is admin's whole contribution to it: its routes, its
 * role vocabulary, its house chrome, and the rooftop list its user can filter
 * to. Adding a searchable entity is a string in `entities`, not a new module.
 *
 * MUST stay module-constant: the `use*` fields below are called as hooks in a
 * fixed order. See defineGlobalSearch's contract.
 */


/**
 * Route gates, mirrored from Routes.tsx so search never links to a page the
 * user can't open. This matters more here than in the dealer portal: admin
 * authenticates as the Hasura admin role, which bypasses row permissions, so
 * these hooks are the ONLY thing keeping records whose detail pages are
 * closed out of the results list (#755 review).
 */
const useGeographyPermitted = () => useFeaturePermission(FeaturePermission.DISPATCHING_GEOGRAPHY);
const useDispatchingPermitted = () => useFeaturePermission(FeaturePermission.DISPATCHING);
const useCustomerPermitted = () => useFeaturePermission(FeaturePermission.CUSTOMER);
const useDriverPermitted = () => useFeaturePermission(FeaturePermission.DRIVER);
const useFinancePermitted = () => useFeaturePermission(FeaturePermission.FINANCE);

const ADMIN_DESK_REACH = { dispatchNotes: true, userRecentMoves: true } as const;
const NO_REACH = {} as const;
/** Module constants above keep the provider's memo stable across renders. */
const useAdminDeskReach = () => (useIsHasuraAdmin() ? ADMIN_DESK_REACH : NO_REACH);

/**
 * Users live under two route trees behind two permissions: staff on /users/:id
 * (USER_MANAGEMENT), portal users on /portal-users/:id (CUSTOMER). The entity
 * stays searchable while the user holds either; each row links only where its
 * own tree is open and renders info-only otherwise. Both hooks run every
 * render so the order stays fixed.
 */
const useUsersPermitted = () => {
  const staff = useFeaturePermission(FeaturePermission.USER_MANAGEMENT);
  const portal = useFeaturePermission(FeaturePermission.CUSTOMER);
  return staff || portal;
};
const useUserHref = () => {
  const staff = useFeaturePermission(FeaturePermission.USER_MANAGEMENT);
  const portal = useFeaturePermission(FeaturePermission.CUSTOMER);
  return React.useCallback(
    (user: { id: number; default_role: string }) =>
      INTERNAL_ROLES.includes(user.default_role)
        ? staff
          ? `/users/${user.id}`
          : undefined
        : portal
          ? `/portal-users/${user.id}`
          : undefined,
    [staff, portal]
  );
};

/**
 * Rooftops a ?customer= may name. Admin's picker fetches its own list, so this
 * exists to validate the URL parameter and label the active-filter chip — an
 * out-of-scope id must not silently zero out every section while the UI still
 * claims an unfiltered search.
 */
const useScopeOptions = (): ScopeOption[] => {
  const { data } = useGetActiveCustomersForSelectQuery();
  return React.useMemo(
    () => (data?.customers ?? []).map(customer => ({ id: customer.id, name: customer.name ?? `Rooftop ${customer.id}` })),
    [data]
  );
};

const SearchEmptyState: React.FC<{ title: string; description: string }> = ({ title, description }) => (
  <EmptyState resource='Results' variant='search' title={title} description={description} icon={<BiSearchAlt />} />
);

export const adminSearchConfig = defineGlobalSearch({
  entities: ['users', 'guests', 'moves', 'customers', 'drivers', 'locations', 'lanes', 'mobility', 'training', 'invoices'],

  // The dispatchnotes reach is legal only for a session on the Hasura admin
  // role, which bypasses permissions — and this portal also admits dealer and
  // dispatcher roles, whose schemas have no such field. Resolved per user, so a
  // dealer-admin signed in here searches without the notes documents instead
  // of failing every users and guests query (see the feature's gql/privileged/).
  usePrivileged: useAdminDeskReach,

  options: {
    users: {
      roleLabel: getRoleLabel,
      // Admin splits drivers by pool: SaaS (lite rooftop), W-2, or gig.
      driverChips: true,
      // Internal staff and portal users live under different route trees —
      // resolved per row against the two permissions (see useUserHref).
      useEnabled: useUsersPermitted,
      useHref: useUserHref,
    },
    guests: { useEnabled: useDispatchingPermitted },
    moves: { useEnabled: useDispatchingPermitted },
    customers: { useEnabled: useCustomerPermitted },
    drivers: { region: true, taxClass: true, useEnabled: useDriverPermitted },
    locations: { region: true, customerRef: true, useEnabled: useGeographyPermitted },
    lanes: { orgName: true, useEnabled: useGeographyPermitted, href: lane => `/lanes/${lane.id}` },
    // /invoices/:id sits behind FINANCE (Routes.tsx).
    invoices: { useEnabled: useFinancePermitted },
    // Mobility: admin has no run detail view, so runs render as info-only cards.
    // Learning Center (#2325): the reader route is auth-only (Routes.tsx) — no
    // gate to mirror (#1109). Admin runs as the Hasura admin role and is
    // unscoped, so it reaches every live module; posters in the private
    // documents bucket resolve through the same presign the library uses.
    training: {
      href: module => `/learning-modules/${encodeURIComponent(module.key)}`,
      resolveAssetUrl,
    },
  },

  useScopeOptions,

  page: {
    homeHref: '/timeline',
    scopeLabel: 'all accounts',
    blankSlateDescription:
      'Search all users, guests, moves, customers, drivers, invoices, and Learning Center training by name, phone number, email address, VIN, move ID, scenario ID, invoice ID, or topic. Press {shortcut} anywhere to start.',
    slots: {
      DataCard,
      PageShell,
      EmptyState: SearchEmptyState,
      RooftopFilter,
    },
  },
});
